import { MainWindowPlatform, StorageAdapter, FileAdapter } from '../platform/types'
import { WindowMessage } from '../types'
import { WebStorageAdapter } from './WebStorageAdapter'
import { WebFileAdapter } from './WebFileAdapter'

export class WebMainWindowPlatform implements MainWindowPlatform {
	private displayWindow: Window | null = null
	private isOpen = false
	private messageHandlers: ((message: WindowMessage) => void)[] = []
	private stateChangeHandlers: ((isOpen: boolean) => void)[] = []
	private storageAdapter = new WebStorageAdapter()
	private fileAdapter = new WebFileAdapter()
	private checkClosedInterval: number | null = null

	constructor() {
		// Listen for messages from display window
		this.handleMessage = this.handleMessage.bind(this)
		window.addEventListener('message', this.handleMessage)
	}

	async openDisplayWindow(title: string): Promise<void> {
		if (this.displayWindow && !this.displayWindow.closed) {
			this.displayWindow.focus()
			return
		}

		const displayWindow = window.open(
			'/?view=display',
			'display-window',
			'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no',
		)

		if (displayWindow) {
			this.displayWindow = displayWindow
			this.isOpen = true
			this.notifyStateChange(true)

			// Start checking if window is closed
			this.startClosedCheck()
		}
	}

	async closeDisplayWindow(): Promise<void> {
		if (this.displayWindow && !this.displayWindow.closed) {
			this.displayWindow.close()
		}
		this.displayWindow = null
		this.isOpen = false
		this.notifyStateChange(false)
		this.stopClosedCheck()
	}

	isDisplayWindowOpen(): boolean {
		return this.isOpen && !!this.displayWindow && !this.displayWindow.closed
	}

	focusDisplayWindow(): void {
		if (this.displayWindow && !this.displayWindow.closed) {
			this.displayWindow.focus()
		}
	}

	onDisplayWindowStateChange(handler: (isOpen: boolean) => void): () => void {
		this.stateChangeHandlers.push(handler)

		// Return cleanup function
		return () => {
			const index = this.stateChangeHandlers.indexOf(handler)
			if (index > -1) {
				this.stateChangeHandlers.splice(index, 1)
			}
		}
	}

	sendToDisplay(message: WindowMessage): void {
		if (this.displayWindow && !this.displayWindow.closed) {
			this.displayWindow.postMessage(message, window.location.origin)
		}
	}

	onMessageFromDisplay(handler: (message: WindowMessage) => void): () => void {
		this.messageHandlers.push(handler)

		// Return cleanup function
		return () => {
			const index = this.messageHandlers.indexOf(handler)
			if (index > -1) {
				this.messageHandlers.splice(index, 1)
			}
		}
	}

	getStorageAdapter(): StorageAdapter {
		return this.storageAdapter
	}

	getFileAdapter(): FileAdapter {
		return this.fileAdapter
	}

	cleanup(): void {
		this.closeDisplayWindow()
		window.removeEventListener('message', this.handleMessage)
		this.stopClosedCheck()
	}

	private handleMessage = (event: MessageEvent) => {
		// Verify origin for security
		if (event.origin !== window.location.origin) return

		// Forward to all registered handlers
		this.messageHandlers.forEach(handler => handler(event.data))
	}

	private startClosedCheck(): void {
		this.stopClosedCheck()
		this.checkClosedInterval = window.setInterval(() => {
			if (this.displayWindow?.closed) {
				this.isOpen = false
				this.displayWindow = null
				this.notifyStateChange(false)
				this.stopClosedCheck()
			}
		}, 1000)
	}

	private stopClosedCheck(): void {
		if (this.checkClosedInterval) {
			clearInterval(this.checkClosedInterval)
			this.checkClosedInterval = null
		}
	}

	private notifyStateChange(isOpen: boolean): void {
		this.stateChangeHandlers.forEach(handler => handler(isOpen))
	}
}
